// The SeaState mark — the client's own vector artwork (images/V1.svg), a single
// filled path: the wave that breaks from the delta's left slope and runs out
// along the trough. This replaces the hand-traced monoline stand-in that stood
// in for it until the real vector existed.
//
// The source file paints a fixed navy (#152e4c). Here the fill is currentColor
// instead, because the mark rides in a header that recolours under the ECDIS
// Day/Dusk/Night themes — a baked hue would stay navy on a black bridge screen.

const VIEW = "0 0 189.2 125.95";
const ASPECT = 189.2 / 125.95; // 1.502 — wider than the stand-in it replaces
const PATH =
  `<path d="M112.54,53.85c5.75-8.96,6.22-17.82,6.23-20.23l-18.03-25.87c-12.11,12.53-17.76,24.59-15.94,34.16,` +
  `1.22,6.39,5.81,11.56,12.92,14.57,16.98,7.66,28.72,19.15,32.27,31.54,2.3,8.02.97,16.07-3.83,23.28l-.1.14c-12.29,` +
  `15.94-29.41,16.21-40.33,11.58-11.67-4.93-18.46-15.56-16.88-26.45,1-6.92,3.89-12.02,8.61-15.15,4.44-2.95,9.61-3.57,` +
  `13.79-3.42-6.64-6.07-13.17-8.85-19.46-8.28-13.85,1.26-25.6,18.12-34.28,32.66-12.76,21.37-28.94,23.57-35.05,23.57-1.42,` +
  `0-2.3-.11-2.46-.14l.36-2.47.36-2.48c.75.1,18.57,2.28,32.5-21.04,9.29-15.56,22.02-33.61,38.12-35.08,9.47-.86,19.04,4.14,` +
  `28.43,14.86.77.87.87,2.12.26,3.11-.6.98-1.76,1.45-2.88,1.15-1.72-.44-10.62-2.46-16.92,1.72-3.48,2.32-5.65,6.25-6.44,` +
  `11.7-1.24,8.61,4.34,17.1,13.89,21.14,9.26,3.92,23.81,3.66,34.36-9.97,3.99-6.01,5.04-12.42,3.14-19.05-3.1-10.82-14.11-` +
  `21.41-29.46-28.34-8.65-3.65-14.29-10.13-15.83-18.21-1.59-8.35.49-22.2,19.44-40.78l2.11-2.07,87.76,125.94h-66.6l5-5h52.02` +
  `l-56.44-81c-.77,4.48-2.52,10.51-6.43,16.61l-4.21-2.7Z"/>`;

/** The mark as an inline SVG string.
 *
 *  `size` still means what it meant for the stand-in — the mark's HEIGHT in the
 *  old near-square box — so every call site keeps its vertical rhythm and only
 *  the width grows to the artwork's true proportions. Passing the real aspect
 *  through rather than squashing it is the whole point of having the vector. */
export function markSvg(size: number): string {
  const h = Math.round(size * 0.94);
  const w = Math.round(h * ASPECT);
  return (
    `<svg width="${w}" height="${h}" viewBox="${VIEW}" ` +
    `fill="currentColor" aria-hidden="true">${PATH}</svg>`
  );
}

/** A span carrying the mark, colored by CSS currentColor. */
export function markEl(size: number): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = "mark";
  s.innerHTML = markSvg(size);
  return s;
}

/** The mark with no intrinsic size — the viewBox alone, for CSS to scale.
 *
 *  The landing lockup wants a mark that grows with the viewport, so it sets the
 *  width in CSS and lets the aspect follow. Same single path as markSvg(): the
 *  landing and the header cannot drift apart, because there is only one of it. */
export function markSvgFluid(): string {
  return `<svg viewBox="${VIEW}" fill="currentColor" aria-hidden="true">${PATH}</svg>`;
}

// The lockup on the landing page is assembled, not shipped as one image: this
// mark, then the wordmark and tagline as real text (see pages/site.ts). Keeping
// them apart is what lets the type stay live — selectable, restyleable, and
// resizable on its own terms — instead of being pixels baked beside the logo.
// web/src/logo.svg is the older combined artwork, no longer used by the app.
